import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, RotateCcw, Upload, AlertTriangle } from 'lucide-react';
import { readManualCocRecords, productsForReport, splitTypedIds, amendDiff } from '../utils/manualCocWorkbook';

/**
 * CpscAmendProducts — CHA-630. Add missed products to a CERTIFIED manual CoC without ever dropping the ones
 * already certified. The operator types ids or drops the lab's workbook (only rows for THIS report are used),
 * sees Added / Removed / Unchanged, ticks each removal individually, and confirms. One reopenCertified call
 * carries the complete list + the confirmed removals; the server refuses any removal not named back.
 *
 * Products only (ruling 4): report-level fields in a workbook — dates, citations, description — are ignored,
 * and the dialog says so. The amended draft then goes Mark ready → Submit → Certify as normal (ruling 3).
 *
 * CHA-669 — the operator can also correct the certificate type, its citation codes and the per-product colours
 * (BV drafts were all assembled as GCC, children's included). Each is sent only when it actually changed, so a
 * products-only amendment sends exactly what it did before.
 */
export default function CpscAmendProducts({ draft, busy, error, onCancel, onConfirm }) {
  const current = useMemo(() => ((draft.productList && draft.productList.length) ? draft.productList : [draft.primaryProductId].filter(Boolean)), [draft]);
  const [typed, setTyped] = useState('');
  const [fromFile, setFromFile] = useState([]);
  const [fileNote, setFileNote] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [removals, setRemovals] = useState([]);
  const firstField = useRef(null);
  // Focus moves into the dialog on open and back to the trigger on close (CodeRabbit #488).
  useEffect(() => {
    const opener = document.activeElement;
    if (firstField.current) firstField.current.focus();
    return () => { if (opener && typeof opener.focus === 'function') opener.focus(); };
  }, []);
  // Escape closes this dialog only — it must not also close the WorklistTable drawer underneath.
  const onKeyDown = (e) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    if (!busy) onCancel();
  };

  const diff = useMemo(() => amendDiff(current, [...splitTypedIds(typed), ...fromFile], removals), [current, typed, fromFile, removals]);

  const inPlace = draft.status !== 'certified'; // an open amendment: same version, products change in place
  // CHA-669 — type / citations / colours are a BV-lane correction. A MANUAL CoC takes them from its upload (where the
  // children's gates run), so its dialog stays products-only, exactly as before (Codex L3 P1 on #501).
  const isBv = draft.source !== 'MANUAL';
  // The dialog replaced BV's bare "Reopen to amend" (Codex L3 P2), so a BV reopen with no edits stays possible —
  // that is how the operator reopens to re-drop the lab's Report-Level export.
  const bareReopen = isBv && !inPlace;

  // CHA-669 — certificate type, citations, colours. Prefilled from the draft (citations = what the upload resolved).
  // Only GCC/CPC are valid; anything else (blank, lowercase legacy) starts unselected so picking a value always counts.
  const currentType = ['GCC', 'CPC'].includes(draft.certificateType) ? draft.certificateType : '';
  const currentCitations = useMemo(() => ((((draft.labs || [])[0] || {}).citationCodes) || []).map(String), [draft]);
  const [certType, setCertType] = useState(currentType);
  const [citationsText, setCitationsText] = useState(currentCitations.join(', '));
  // Each product starts at the colour it would be filed with today: its own, else the draft-level fallback.
  const shownColor = (p) => String((draft.productColors || {})[p] || draft.color || '').trim();
  const [colors, setColors] = useState({}); // only what the operator typed; everything else shows shownColor
  const colorOf = (p) => (Object.prototype.hasOwnProperty.call(colors, p) ? colors[p] : shownColor(p));
  const citations = useMemo(() => splitTypedIds(citationsText), [citationsText]);
  const typeChanged = certType !== currentType;
  const citationsChanged = citations.join('|') !== currentCitations.join('|');
  const finalColors = useMemo(() => Object.fromEntries(diff.finalList.map((p) => [p, String(colorOf(p)).trim()]).filter(([, c]) => c)), [diff.finalList, colors]); // eslint-disable-line react-hooks/exhaustive-deps
  const colorsChanged = diff.finalList.some((p) => (finalColors[p] || '') !== shownColor(p));

  const productsChanged = diff.added.length > 0 || diff.removed.length > 0;
  const citationsCleared = citationsChanged && !citations.length; // the server refuses an empty list; untouched = kept
  const changed = (productsChanged || typeChanged || citationsChanged || colorsChanged || bareReopen)
    && diff.finalList.length > 0 && !citationsCleared; // a certificate needs ≥1 product and ≥1 citation

  const onFile = async (file) => {
    if (!file) return;
    setFileError(null); setFileNote(null);
    try {
      const parsed = readManualCocRecords(await file.arrayBuffer());
      if (parsed.error) { setFileError(parsed.error); return; }
      const { productIds, otherReports } = productsForReport(parsed.records, draft.reportNo);
      if (!productIds.length) { setFileError(`No rows for report ${draft.reportNo} in that workbook.`); return; }
      setFromFile(productIds);
      setFileNote(`${productIds.length} product(s) for ${draft.reportNo} read from ${file.name}. Only Product IDs are used — dates, citations and descriptions in the workbook are ignored.`
        + (otherReports.length ? ` Rows for ${otherReports.length} other report(s) (${otherReports.slice(0, 3).join(', ')}${otherReports.length > 3 ? '…' : ''}) were ignored — amend each report on its own draft.` : ''));
    } catch (e) { setFileError(e?.message || 'Could not read the file.'); }
  };

  const toggleRemoval = (p) => setRemovals((r) => (r.includes(p) ? r.filter((x) => x !== p) : [...r, p]));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Amend products" onKeyDown={onKeyDown}>
      <div className="bg-carbon-white border border-carbon-border w-full max-w-2xl max-h-[90vh] overflow-y-auto font-plex text-carbon-ink">
        <div className="px-5 py-3 border-b border-carbon-border">
          <h3 className="text-base font-semibold">Amend products — {draft.reportNo}</h3>
          <p className="text-xs text-carbon-gray-70 mt-0.5">
            {inPlace
              ? <>Open amendment <strong>{draft.versionId}</strong> (amended from {draft.amendedFromVersionId}). Products change on this version; mark it ready again afterwards.</>
              : <>Certified version <strong>{draft.versionId}</strong> stays live at CPSC until you certify the new version.</>}
            {' '}Every listed product is kept unless you tick it for removal.
          </p>
        </div>

        <div className="px-5 py-3 space-y-3 text-sm">
          <label className="block">
            <span className="text-xs font-semibold text-carbon-gray-80">Add Product IDs (comma or one per line)</span>
            <textarea ref={firstField} aria-label="Add Product IDs" value={typed} onChange={(e) => setTyped(e.target.value)} rows={3}
              className="mt-1 w-full border border-carbon-gray-30 px-2 py-1 font-plex-mono text-xs" placeholder="e.g. ABC123-RED, ABC124-BLUE" />
          </label>
          <label className="flex items-center gap-2 text-xs text-carbon-blue-60 cursor-pointer">
            <Upload size={12} /> Or read them from the lab's Manual CoC workbook
            <input type="file" accept=".xlsx,.xls" className="hidden" aria-label="Amend workbook" onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
          {fileNote && <div className="text-xs text-carbon-gray-80 bg-carbon-blue-10 border border-carbon-blue-60 px-2 py-1">{fileNote}</div>}
          {fileError && <div className="text-xs text-carbon-red-60 flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5" />{fileError}</div>}

          <div>
            <div className="text-xs font-semibold text-carbon-green-50">Added ({diff.added.length})</div>
            <div className="font-plex-mono text-xs">{diff.added.join(', ') || '—'}</div>
          </div>
          <div>
            <div className="text-xs font-semibold text-carbon-gray-80">{inPlace ? 'Current' : 'Certified'} products ({current.length}) — tick only what must come OFF the certificate</div>
            <ul className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5">
              {current.map((p) => (
                <li key={p}>
                  <label className="flex items-center gap-1.5 font-plex-mono text-xs">
                    <input type="checkbox" aria-label={`Remove ${p}`} checked={removals.includes(p)} onChange={() => toggleRemoval(p)} />
                    <span className={removals.includes(p) ? 'line-through text-carbon-red-60' : ''}>{p}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
          {diff.removed.length > 0 && (
            <div className="text-xs text-carbon-red-60 bg-carbon-white border border-carbon-red-60 px-2 py-1">
              Removing {diff.removed.length} certified product(s): {diff.removed.join(', ')}. They will not be on the new certificate.
            </div>
          )}
          <div className="text-xs text-carbon-gray-70">New version will carry <strong>{diff.finalList.length}</strong> product(s): {diff.unchanged.length} unchanged, {diff.added.length} added, {diff.removed.length} removed.</div>

          {/* CHA-669 — certificate type + citations + colours (BV only) */}
          {isBv && (<>
          <div className="border-t border-carbon-border pt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-carbon-gray-80">Certificate type</span>
              <select aria-label="Certificate type" value={certType} onChange={(e) => setCertType(e.target.value)}
                className="mt-1 w-full border border-carbon-gray-30 px-2 py-1 text-xs">
                {!currentType && <option value="">—</option>}
                <option value="GCC">GCC (general use)</option>
                <option value="CPC">CPC (children's product)</option>
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs font-semibold text-carbon-gray-80">Citation codes (from the upload — match the lab report)</span>
              <input aria-label="Citation codes" value={citationsText} onChange={(e) => setCitationsText(e.target.value)}
                className="mt-1 w-full border border-carbon-gray-30 px-2 py-1 font-plex-mono text-xs" placeholder="e.g. 1610, 1303, 1278a" />
            </label>
          </div>
          {typeChanged && <div className="text-xs text-carbon-orange-70">Certificate type changes from {currentType || '—'} to {certType}.{certType === 'CPC' ? ' A CPC needs the lab CPSC ID and the children\'s citations (e.g. 1278a).' : ''}</div>}
          {citationsCleared && <div className="text-xs text-carbon-red-60">A certificate needs at least one citation code.</div>}
          <div>
            <div className="text-xs font-semibold text-carbon-gray-80">Colours</div>
            <ul className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
              {diff.finalList.map((p) => (
                <li key={p} className="flex items-center gap-1.5">
                  <span className="font-plex-mono text-xs w-32 shrink-0 truncate">{p}</span>
                  <input aria-label={`Colour for ${p}`} value={colorOf(p)} onChange={(e) => setColors((c) => ({ ...c, [p]: e.target.value }))}
                    className="flex-1 min-w-0 border border-carbon-gray-30 px-1.5 py-0.5 text-xs" />
                </li>
              ))}
            </ul>
          </div>
          </>)}
        </div>

        {error && <div className="mx-5 mb-2 text-xs text-carbon-red-60 border border-carbon-red-60 px-2 py-1 flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{error}</div>}
        <div className="px-5 py-3 border-t border-carbon-border flex justify-end gap-2">
          <button onClick={onCancel} disabled={busy} className="px-3 py-1 text-xs border border-carbon-gray-30 hover:bg-carbon-gray-10 disabled:opacity-50">Cancel</button>
          <button
            onClick={() => onConfirm({
              productList: diff.finalList, confirmRemovals: diff.removed,
              ...(isBv && typeChanged ? { certificateType: certType } : {}),
              ...(isBv && citationsChanged ? { citationCodes: citations } : {}),
              ...(isBv && colorsChanged ? { productColors: finalColors } : {}),
            })}
            disabled={busy || !changed}
            title={changed ? '' : 'Change a product, the certificate type, a citation or a colour first'}
            className="flex items-center gap-1 px-3 py-1 text-xs font-semibold bg-carbon-blue-60 text-white hover:bg-carbon-blue-70 disabled:opacity-50"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} {inPlace ? 'Update products' : 'Reopen as new version'} with {diff.finalList.length} product(s)
          </button>
        </div>
      </div>
    </div>
  );
}
